import { FormikContextType, useFormikContext } from "formik";
import { parsePhoneNumber } from "libphonenumber-js";
import React, { Suspense, useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@redux/dispatcher";
import { load } from "@redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.module";
import * as PhonenumberPrefixesSelectors from "@redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { PrefixedInputField } from "@components/Form/FormFields/PrefixedInput/prefixedInput.component";

export type PhoneFieldProps = {
    name: string;
    label: string;
    placeholder: string;
    required: boolean;
    autoComplete?: string;
    validationRule?: any;
    test?: string;
    disabled?: boolean;
};

type PhonePrefixOptions = {
    isoCode?: string;
    label:
        | string
        | React.DetailedHTMLProps<
              React.HTMLAttributes<HTMLHRElement>,
              HTMLHRElement
          >;
    value?: string;
    isDisabled?: boolean;
};

const PhoneField = ({
    name,
    label,
    placeholder,
    required,
    autoComplete,
    validationRule,
    test,
    disabled,
}: PhoneFieldProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const { setValues, values }: FormikContextType<any> = useFormikContext();
    const prefixName = name + "Prefix";
    const isLoadingPhonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.isLoading,
    );
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual,
    );

    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        dispatch(load());
        setHasLoaded(true);
    }, [dispatch]);

    useEffect(() => {
        if (!values[prefixName] && values[name]) {
            let phone;

            if (values[name]) {
                try {
                    phone = parsePhoneNumber(values[name] || "");
                } catch (_) {}
            }

            setValues({
                ...values,
                [prefixName]: `+${phone?.countryCallingCode || "32"}`,
                [name]: phone?.nationalNumber || "",
            });
        } else {
            setValues({
                ...values,
                [prefixName]: values[prefixName] || "+32",
            });
        }
    }, [values[name]]);

    const getPrefix = (prefix: string) => {
        return phonenumberPrefixes.find(p => p.prefix === prefix)?.country
            .isoCode;
    };

    const onBlur = () => {
        let phone;
        let isValid = false;

        if (values[name]) {
            try {
                phone = parsePhoneNumber(
                    values[name] || "",
                    getPrefix(values[prefixName]),
                );
                isValid = phone.isValid();
            } catch (_) {
                isValid = false;
            }
        }

        if (!isValid || !phone) {
            return;
        }

        setValues({
            ...values,
            [prefixName]: `+${phone.countryCallingCode}`,
            [name]: phone.nationalNumber,
        });
    };

    const options: Array<PhonePrefixOptions> = phonenumberPrefixes.map(item => {
        return {
            isoCode: item.country.isoCode,
            label: item.country.name,
            value: item.prefix,
        };
    });

    // Add Divider
    options?.splice(5, 0, {
        label: <hr />,
        isDisabled: true,
    });

    const renderOption = (option: PhonePrefixOptions) => {
        if (!option.isoCode) {
            return option.label;
        }

        return (
            <div className="text-nowrap">
                <ReactCountryFlag
                    svg
                    countryCode={option.isoCode}
                    style={{
                        width: "24px",
                        height: "24px",
                    }}
                />
                <span className="hide-in-control pl-2 text-muted">
                    {option.label as string}
                </span>
                <span className="pl-2">{option.value}</span>
            </div>
        );
    };

    if (
        !phonenumberPrefixes?.length &&
        !isLoadingPhonenumberPrefixes &&
        hasLoaded
    ) {
        return null;
    }

    return (
        <PrefixedInputField
            test={test}
            name={name}
            onBlur={onBlur}
            prefixName={prefixName}
            label={label}
            prefixOptions={options}
            required={required}
            validationRule={validationRule}
            renderOption={renderOption}
            placeholder={placeholder}
            autoComplete={autoComplete}
            type="tel"
            disabled={disabled}
        />
    );
};

export { PhoneField };
